<script setup>
// Knowledge coverage (phase 4.5): the kit's Card with its Progress (the "next steps" bar of the kit) and the three
// numbers that lead to their pages (approved FAQs, pending FAQs, documents). Same data as before.
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ArrowUpRight16 } from '@carbon/icons-vue';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardAction,
} from 'dashboard/components-next/polaris/ui/card';
import { Progress } from 'dashboard/components-next/polaris/ui/progress';

const props = defineProps({
  knowledge: {
    type: Object,
    default: () => ({ approved: 0, pending: 0, documents: 0, coverage: 0 }),
  },
});

const { t } = useI18n();
const route = useRoute();
const { formatFullNumber } = useNumberFormatter();

const approvedPct = computed(() => props.knowledge.coverage ?? 0);

const linkTo = routeName => ({
  name: routeName,
  params: {
    accountId: route.params.accountId,
    assistantId: route.params.assistantId,
  },
});

const stats = computed(() => [
  {
    key: 'approved',
    value: props.knowledge.approved,
    label: t('POLARIS.OVERVIEW.KNOWLEDGE.APPROVED'),
    to: linkTo('polaris_assistants_responses_index'),
  },
  {
    key: 'pending',
    value: props.knowledge.pending,
    label: t('POLARIS.OVERVIEW.KNOWLEDGE.PENDING'),
    to: linkTo('polaris_assistants_responses_pending'),
  },
  {
    key: 'documents',
    value: props.knowledge.documents,
    label: t('POLARIS.OVERVIEW.KNOWLEDGE.DOCUMENTS'),
    to: linkTo('polaris_assistants_documents_index'),
  },
]);
</script>

<template>
  <Card class="gap-4 py-5" data-knowledge-card>
    <CardHeader class="px-5">
      <CardTitle>{{ t('POLARIS.OVERVIEW.KNOWLEDGE.TITLE') }}</CardTitle>
      <CardAction
        class="tabular-nums type-polaris-body text-polaris-muted-foreground"
      >
        {{ t('POLARIS.OVERVIEW.KNOWLEDGE.COVERAGE', { pct: approvedPct }) }}
      </CardAction>
    </CardHeader>
    <CardContent class="grid gap-4 px-5">
      <Progress
        :model-value="approvedPct"
        :aria-label="t('POLARIS.OVERVIEW.KNOWLEDGE.TITLE')"
      />
      <div class="grid grid-cols-3 gap-3">
        <router-link
          v-for="stat in stats"
          :key="stat.key"
          :to="stat.to"
          class="group/stat grid min-w-0 gap-0.5 rounded-polaris-md outline-none focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
        >
          <span class="tabular-nums type-polaris-section-title">
            {{ formatFullNumber(stat.value ?? 0) }}
          </span>
          <span
            class="inline-flex items-center gap-1 type-polaris-caption text-polaris-muted-foreground group-hover/stat:text-polaris-foreground"
          >
            <span class="truncate">{{ stat.label }}</span>
            <ArrowUpRight16
              class="size-3 shrink-0 opacity-0 transition-opacity group-hover/stat:opacity-100"
              aria-hidden="true"
            />
          </span>
        </router-link>
      </div>
    </CardContent>
  </Card>
</template>
